import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect, useCallback } from 'react';
import { rbacAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Save, Users, Building2, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

export const RBACSettingsPage = () => {
  usePageTitle('Access Control');
  const { isAdmin } = useAuth();
  const [menuItems, setMenuItems] = useState([]);
  const [config, setConfig] = useState({ admin: [], manager: [], staff: [], client: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [itemsRes, configRes] = await Promise.all([
        rbacAPI.getMenuItems(),
        rbacAPI.getConfig(),
      ]);
      setMenuItems(itemsRes.data);
      setConfig({
        admin: configRes.data.admin || [],
        manager: configRes.data.manager || [],
        staff: configRes.data.staff || [],
        client: configRes.data.client || [],
      });
    } catch { toast.error('Failed to load RBAC config'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (role, key) => {
    setConfig(prev => {
      const current = prev[role] || [];
      const updated = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
      return { ...prev, [role]: updated };
    });
  };

  const selectAll = (role) => {
    setConfig(prev => ({ ...prev, [role]: menuItems.map(m => m.key) }));
  };

  const deselectAll = (role) => {
    setConfig(prev => ({ ...prev, [role]: [] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await rbacAPI.updateConfig(config);
      toast.success('Access control saved. Changes will reflect on next login.');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  const roles = [
    { key: 'admin', label: 'Admin', icon: Shield, color: 'from-rose-600 to-pink-600', desc: 'Organization admins (full operational access)' },
    { key: 'manager', label: 'Manager', icon: Users, color: 'from-amber-600 to-orange-600', desc: 'Team managers (approve leaves, verify tasks)' },
    { key: 'staff', label: 'Staff / Consultant', icon: Users, color: 'from-blue-600 to-indigo-600', desc: 'Consultants and staff members' },
    { key: 'client', label: 'Client', icon: Building2, color: 'from-emerald-600 to-teal-600', desc: 'Hospital and lab clients' },
  ];

  return (
    <div className="space-y-6" data-testid="rbac-settings-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-500 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Shield className="w-7 h-7" /> Access Control</h1>
            <p className="text-white/80 text-sm mt-1">Configure which pages each role can access in the sidebar</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-white text-purple-700 hover:bg-purple-50" data-testid="save-rbac-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
        Only Super Admin can configure access. Super Admin always has full access. Configure which sidebar pages are visible to Admin, Manager, Staff, and Client roles below.
      </p>

      {/* Role Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {roles.map(role => (
          <Card key={role.key} className="overflow-hidden" data-testid={`rbac-card-${role.key}`}>
            <CardHeader className={`bg-gradient-to-r ${role.color} text-white pb-3`}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <role.icon className="w-5 h-5" /> {role.label}
                </CardTitle>
                <Badge className="bg-white/20 text-white">{config[role.key]?.length || 0} / {menuItems.length}</Badge>
              </div>
              <p className="text-sm text-white/70">{role.desc}</p>
              <div className="flex gap-2 mt-2">
                <Button variant="ghost" size="sm" className="text-white/80 hover:text-white hover:bg-white/10 h-7 text-xs" onClick={() => selectAll(role.key)} data-testid={`select-all-${role.key}`}>
                  Select All
                </Button>
                <Button variant="ghost" size="sm" className="text-white/80 hover:text-white hover:bg-white/10 h-7 text-xs" onClick={() => deselectAll(role.key)} data-testid={`deselect-all-${role.key}`}>
                  Deselect All
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {menuItems.map(item => {
                  const checked = (config[role.key] || []).includes(item.key);
                  return (
                    <label
                      key={item.key}
                      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors hover:bg-gray-50 ${checked ? 'bg-green-50/50' : ''}`}
                      data-testid={`rbac-toggle-${role.key}-${item.key}`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggle(role.key, item.key)} />
                      <div className="flex-1 flex items-center justify-between">
                        <span className={`text-sm ${checked ? 'font-medium text-gray-900' : 'text-gray-500'}`}>{item.label}</span>
                        <span className="text-xs text-gray-400 font-mono">{item.path}</span>
                      </div>
                      {checked && <Check className="w-4 h-4 text-green-500 shrink-0" />}
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default RBACSettingsPage;
